import {darkTokens, lightTokens, ModeTokens, motion, overlay, radii, shadows} from "./tokens";
import {defaultBrandRoles} from "./brandRoles";

/**
 * CSS-variable token bridge — stamps the v3 design tokens (`./tokens.ts`) as `--tf-*` custom
 * properties so CSS Modules can consume `var(--tf-*)` without importing the token module or reaching
 * for styled-components. The generated `<style>` is a pure function of the static token constants, so
 * its text is byte-identical on the server (SSG pre-render) and the first client render — see
 * {@link themeVarsStyleText}. Light is the default (`:root`); the dark values only override once
 * `data-theme="dark"` is stamped on the document element (owned by `ThemeModeProvider`).
 */

/** camelCase / alphanumeric token key -> kebab-case CSS-var segment (`cardBorder` -> `card-border`). */
function kebabCase(key: string): string {
	return key.replace(/([A-Z])/g, "-$1").toLowerCase();
}

/**
 * The per-mode `--tf-*` custom properties for one {@link ModeTokens} set: every color/shadow/gradient
 * field (all fields except the `mode` discriminator) as `--tf-<kebab-key>`. These are exactly the
 * values that differ between light and dark, so they are emitted once per mode by
 * {@link themeVarsStyleText}.
 *
 * The element ROLES (`brandRoles.ts`, kitecorp/transferify-ui#2935) are left out: a signed-in account's
 * colour replaces them on the THEME, which a static stylesheet never sees, so a bridged role would keep
 * painting today's colour in a CSS Module while every styled-component beside it followed the account.
 */
export function themeCssVars(tokens: ModeTokens): Record<string, string> {
	const roles = new Set(Object.keys(defaultBrandRoles(tokens)));
	const vars: Record<string, string> = {};
	for (const [key, value] of Object.entries(tokens)) {
		if (key === "mode" || roles.has(key)) {
			continue;
		}
		vars[`--tf-${kebabCase(key)}`] = value as string;
	}
	return vars;
}

/**
 * The mode-independent `--tf-*` custom properties: the radius scale (`--tf-radius-*`), the static
 * elevation shadows (`--tf-shadow-*`), the overlay scrim (`--tf-scrim`) and the motion tokens
 * (`--tf-motion-*` / `--tf-ease-*`). Emitted once, on the default `:root` block.
 */
export function staticCssVars(): Record<string, string> {
	const vars: Record<string, string> = {};
	for (const [key, value] of Object.entries(radii)) {
		vars[`--tf-radius-${kebabCase(key)}`] = value;
	}
	for (const [key, value] of Object.entries(shadows)) {
		vars[`--tf-shadow-${kebabCase(key)}`] = value;
	}
	vars["--tf-scrim"] = overlay.scrim;
	vars["--tf-motion-enter"] = motion.enter;
	vars["--tf-motion-exit"] = motion.exit;
	vars["--tf-motion-hover"] = motion.hover;
	vars["--tf-ease-out"] = motion.easeOut;
	vars["--tf-ease-in"] = motion.easeIn;
	return vars;
}

/** Serialize a var map into a CSS rule body (one tab-indented `--k: v;` declaration per line). */
function declarations(vars: Record<string, string>): string {
	return Object.entries(vars)
		.map(([name, value]) => `\t${name}: ${value};`)
		.join("\n");
}

/**
 * The complete, mode-agnostic `<style>` body for the token bridge. Light lives on `:root` so it
 * applies on the very first paint with no attribute set — matching the SSG/first-client-render
 * "always light" invariant — and the dark values override under `:root[data-theme="dark"]` once the
 * mode is resolved on the client. Because it depends only on the static token constants, the output
 * is deterministic and identical on server and client (no hydration mismatch).
 */
export function themeVarsStyleText(light: ModeTokens, dark: ModeTokens): string {
	const root = {...staticCssVars(), ...themeCssVars(light)};
	const darkOverrides = themeCssVars(dark);
	return [
		`:root {\n${declarations(root)}\n}`,
		`:root[data-theme="dark"] {\n${declarations(darkOverrides)}\n}`,
	].join("\n");
}

/**
 * The concrete bridge `<style>` text for the app's light/dark token sets. Computed once at module
 * scope (no `window`/`document` access) and rendered as a static `<style>` by `AppTheme`.
 */
export const THEME_VARS_STYLE = themeVarsStyleText(lightTokens, darkTokens);
